// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import type { ListingMini as OrgListingMini } from 'api/orgs';
import type { ListingMini as GroupListingMini } from 'api/groups';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE } from 'utils/constants';
import { RouterLink, ArrowLink } from 'components/Link';
import { H4, BodySmall } from 'components/Text';
import { humanDate } from 'utils/date';
import ToggleHelperContainer from 'containers/ToggleHelperContainer';
import Container from 'components/Container';
import styled from 'styled-components';
import { getCityStateString } from 'utils/address';

import { colors, fontWeights, breakpoints } from '../theme';

const MAX_LISTINGS = 10;

const StyledTable = styled.table`
  width: 100%;
  text-align: left;
  border-spacing: 10px 11px;
  margin-top: -11px;
  @media (min-width: ${breakpoints[2]}) {
    border-spacing: 40px 11px;
  }
  td {
    color: ${colors.battleshipGrey};
  }
  th {
    font-size: 18px;
    color: ${colors.greyishBrown};
    padding-top: 10px;
    padding-bottom: 32px;
    font-weight: ${fontWeights.semiBold};
    border-top: solid 1px ${colors.silver};
  }
  td,
  th {
    padding: 0;
    &:nth-child(2) {
      width: 180px;
    }
    &:nth-child(3) {
      width: 160px;
    }
  }
`;

const Listings = ({
  listingType,
  listings,
  ...props
}: {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'IDEALIST_DAY_EVENT',
  listings: Array<OrgListingMini | GroupListingMini>
}) => (
  <Container mx={['-10px', '-10px', '-10px', '-40px']} {...props}>
    <StyledTable>
      <thead>
        <tr>
          <H4 tag="th" pt="11px" pb="28px">
            {LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE[listingType]}
          </H4>
          <H4 tag="th" pt="11px" pb="28px">
            {getText('Location')}
          </H4>
          <H4 tag="th" pt="11px" pb="28px">
            {getText('Date Posted')}
          </H4>
        </tr>
      </thead>
      <tbody>
        {listings.map(listing => (
          <tr key={listing.id}>
            <BodySmall tag="td" pb="7px">
              <RouterLink to={listing.url[USER_LOCALE]}>
                {listing.name}
              </RouterLink>
            </BodySmall>
            <BodySmall tag="td">
              {listing.isVirtual
                ? 'Online/Virtual'
                : getCityStateString(listing.address)}
            </BodySmall>
            <BodySmall tag="td">{humanDate(listing.firstPublished)}</BodySmall>
          </tr>
        ))}
      </tbody>
    </StyledTable>
  </Container>
);

const TruncatedListings = ({
  listingType,
  listings
}: {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'IDEALIST_DAY_EVENT',
  listings: Array<OrgListingMini | GroupListingMini>
}) => {
  const hasMore = listings.length > MAX_LISTINGS;
  if (hasMore) {
    return (
      <ToggleHelperContainer>
        {(showMore, toggleShowMore) => (
          <>
            <Listings
              listingType={listingType}
              listings={showMore ? listings : listings.slice(0, MAX_LISTINGS)}
              mb="10px"
            />
            <ArrowLink
              href="#"
              onClick={e => {
                e.preventDefault();
                toggleShowMore();
              }}
              open={showMore}
            >
              {showMore
                ? getText('View less')
                : sprintf(getText('View %(count)s more'), {
                    count: listings.length - MAX_LISTINGS
                  })}
            </ArrowLink>
          </>
        )}
      </ToggleHelperContainer>
    );
  }
  return <Listings listingType={listingType} listings={listings} />;
};

export default TruncatedListings;
